import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { casestudy } from "../constants";
import { ProjectProp } from "@/types/types";
import { Link, useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

export default function Test() {
  const navigate = useNavigate();
  const desktopRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  // DESKTOP ANIMATION
  useEffect(() => {
    const ctx = gsap.context(() => {
      const slides = gsap.utils.toArray(".desktop-slide") as HTMLElement[];
      gsap.set(slides, { autoAlpha: 0 });

      const tl = gsap.timeline({ repeat: -1 });

      slides.forEach((slide) => {
        const left = slide.querySelector(".left-half") as HTMLElement;
        const right = slide.querySelector(".right-half") as HTMLElement;
        const text = slide.querySelector(".slide-text") as HTMLElement;

        // Animate IN
        tl.add(() => {
          gsap.set(slide, { autoAlpha: 1 });
          gsap.fromTo(
            slide,
            { scale: 1.05, opacity: 0 },
            { scale: 1, opacity: 1, duration: 1.2, ease: "power2.out" },
          );

          gsap.fromTo(
            left,
            { y: "110%" },
            { y: "0%", duration: 2, ease: "power3.out" },
          );
          gsap.fromTo(
            right,
            { y: "-110%" },
            { y: "0%", duration: 2, ease: "power3.out" },
          );
          gsap.fromTo(
            text,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.8, delay: 0.6 },
          );
        });

        // Show for 3.5 seconds
        tl.to({}, { duration: 3.5 });

        // Animate OUT (opposite direction)
        tl.add(() => {
          gsap.to(left, { y: "110%", duration: 1.5 });
          gsap.to(right, { y: "-110%", duration: 1.5 });
          gsap.to(text, {
            opacity: 0,
            y: -30,
            duration: 0.6,
            ease: "power2.in",
          });
          gsap.to(slide, {
            opacity: 0,
            scale: 1.05,
            duration: 1,
            ease: "power2.in",
            onComplete: () => gsap.set(slide, { autoAlpha: 0 }),
          });
        });

        // Small gap before next starts
        tl.to({}, { duration: 0.4 });
      });

      ScrollTrigger.create({
        trigger: desktopRef.current,
        start: "top top",
        end: "bottom top",
        scrub: true,
        onUpdate: (self) => {
          gsap.to(desktopRef.current, {
            backgroundPositionY: `${self.progress * 50}%`,
            ease: "none",
          });
        },
      });
    }, desktopRef);

    return () => ctx.revert();
  }, []);

  // MOBILE ANIMATION (HORIZONTAL BREAK TOP & BOTTOM)
  useEffect(() => {
    const ctx = gsap.context(() => {
      const slides = gsap.utils.toArray(".mobile-slide") as HTMLElement[];
      gsap.set(slides, { autoAlpha: 0 });

      const tl = gsap.timeline({ repeat: -1 });

      slides.forEach((slide) => {
        const topHalf = slide.querySelector(".top-half") as HTMLElement;
        const bottomHalf = slide.querySelector(".bottom-half") as HTMLElement;
        const text = slide.querySelector(".slide-text") as HTMLElement;

        tl.add(() => {
          gsap.set(slide, { autoAlpha: 1 });
          gsap.fromTo(
            slide,
            { scale: 1.05, opacity: 0 },
            { scale: 1, opacity: 1, duration: 1.2, ease: "power2.out" },
          );
          gsap.fromTo(
            topHalf,
            { x: "-110%" },
            { x: "0%", duration: 2, ease: "power3.out" },
          );
          gsap.fromTo(
            bottomHalf,
            { x: "110%" },
            { x: "0%", duration: 2, ease: "power3.out" },
          );
          gsap.fromTo(
            text,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.8, delay: 0.6 },
          );
        });

        // Show for 3 seconds
        tl.to({}, { duration: 3.5 });

        // Animate OUT (opposite direction)
        tl.add(() => {
          gsap.to(topHalf, { x: "-110%", duration: 1.5 });
          gsap.to(bottomHalf, { x: "110%", duration: 1.5 });
          gsap.to(text, {
            opacity: 0,
            y: -30,
            duration: 0.6,
            ease: "power2.in",
          });
          gsap.to(slide, {
            opacity: 0,
            scale: 1.05,
            duration: 1,
            ease: "power2.in",
            onComplete: () => gsap.set(slide, { autoAlpha: 0 }),
          });
        });

        // Small gap before next starts
        tl.to({}, { duration: 0.4 });
      });
    }, mobileRef);

    return () => ctx.revert();
  }, []);

  const handleClick = (current: ProjectProp) => {
    // setProject(current);
    // setIsCaseStudyModalOpen(true);
    navigate("/brandgrowthsolution");
  };

  return (
    <div id="works">
      {/* DESKTOP VERSION */}
      <section
        ref={desktopRef}
        className="hidden 2xl:block relative w-full h-screen bg-[#0D1821] overflow-hidden"
      >
        {casestudy.map((study) => (
          <div
            key={study.id}
            className="desktop-slide absolute top-0 left-0 w-full h-full"
          >
            <div
              className="left-half absolute top-0 left-0 w-[49.8%] h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${study.image1})` }}
            ></div>
            <div
              className="right-half absolute top-0 right-0 w-[49.8%] h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${study.image2})` }}
            ></div>

            {/* Gradient Overlay for Text */}
            <div className="absolute bottom-0 left-0 w-[49.8%] h-1/3 bg-gradient-to-t from-[#0d1821]/70 to-transparent pointer-events-none"></div>

            <div className="slide-text absolute bottom-16 left-16 z-20 max-w-[600px]">
              <h2 className="text-[#f5f5f5] text-4xl mb-4 max-w-[500px]">
                {study.title}
              </h2>
              <Link
                to={`?id=${study.id}`}
                className="w-12 h-12 rounded-lg text-[#0d1821] flex justify-center items-center bg-[#c3f620] hover:text-[#c3f620] hover:bg-[#0d1821] transition"
              >
                →
              </Link>
            </div>
          </div>
        ))}
      </section>

      {/* MOBILE VERSION */}
      <section
        ref={mobileRef}
        className="block 2xl:hidden relative w-full h-screen bg-[#f5f5f5] overflow-hidden"
      >
        {casestudy.map((study) => (
          <div
            key={study.id}
            className="mobile-slide absolute top-0 left-0 w-full h-full"
          >
            <div
              className="top-half absolute top-0 left-0 w-full h-[49.7%] bg-cover bg-center"
              style={{ backgroundImage: `url(${study.image1})` }}
            ></div>
            <div
              className="bottom-half absolute bottom-0 left-0 w-full h-[49.7%] bg-cover bg-center"
              style={{ backgroundImage: `url(${study.image2})` }}
            ></div>
            <div className="absolute bottom-0 left-0 w-full h-1/4 bg-gradient-to-t from-[#0d1821]/70 to-transparent pointer-events-none"></div>
            <div className="slide-text absolute bottom-12 left-6 z-20">
              <h2 className="text-[#f5f5f5] text-2xl md:text-4xl mb-2">
                {study.title}
              </h2>
              <Link
                to={`?id=${study.id}`}
                className="w-10 h-10 rounded-lg text-[#0d1821] flex justify-center items-center bg-[#c3f620] hover:text-[#c3f620] hover:bg-[#0d1821] transition"
              >
                →
              </Link>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
